import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { TreeNode } from 'primeng/api';

import { createCsv } from '../../../../common/components/reactive-form-builder/export/createCsv';
import { FormType } from '../../../../common/components/reactive-form-builder/interfaces/form.interface';
import { ColInterface } from '../../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../../common/components/common-list-table/col-styles.interface';
import { StorageManager } from '../../../../common/storage-manager.class';
import { Utils } from '../../../../common/utils';
import { RequestHandler } from '../../../../service/OffService/request-handler';
import { TareasApiService } from '../../../../service/api/tareas-api.service';
import { DashboardService } from '../../../../service/events/dashboard.service';
import { Filtering } from '../../../../service/filtering/filtering';
import { BaseView } from '../../../base-view';
import { environment } from '../../../../../environments/environment';
import { AgroambientalModel } from '../../../../models/tareas/agroambientales.model';
import { AgroambientalesFormDefinition } from './agroambientales-form/form-control/form-definition';

@Component({
    selector: 'app-agroambientales',
    standalone: false,
    templateUrl: './agroambientales.component.html',
    styleUrls: ['./agroambientales.component.scss'],
})
export class AgroambientalesComponent extends BaseView<AgroambientalModel> {
    public readonly showTareasBioExportCsv = environment.features.showTareasBioExportCsv;
    public readonly appName = environment.appName;
    public readonly serverUrl = environment.serverUrl;
    public readonly rolUser = StorageManager.getUser().rol;
    public readonly fincaName = environment.features.fincaName;
    public readonly sectorName = environment.features.sectorName;
    public readonly parcelaName = environment.features.parcelaName;

    public getRequest!: RequestHandler<AgroambientalModel[]>;
    public deleteRequest!: RequestHandler<AgroambientalModel>;

    public pathToForm = 'tareas-crear-agroambiental';
    public pathToEdit = 'tareas-editar-agroambiental';
    public pathToDuplicate = 'tareas-duplicar-agroambiental';

    public form: AgroambientalesFormDefinition | null = null;
    public override filtering: Filtering<AgroambientalModel> = new Filtering<AgroambientalModel>();
    public override canShow: Record<string, boolean> = {};
    public override model: AgroambientalModel = {};

    public cols: ColInterface[] = [
        { field: 'finca', header: this.fincaName },
        { field: 'sector', header: this.sectorName },
        { field: 'parcelas', header: this.parcelaName },
        { field: 'fecha_establecimiento_cv', header: 'F. Establecimiento C. Vegetal' },
        { field: 'nombre_mantenimiento', header: 'Tipo Mantenimiento C. Vegetal' },
        { field: 'anchura_cv', header: 'Anchura C. Vegetal' },
        { field: 'anchura_libre_proyeccion_1', header: 'Anchura Libre Proyección V.' },
        { field: 'fecha_establecimiento_ci', header: 'F. Establecimiento C. Inerte' },
        { field: 'anchura_ci', header: 'Tipo Mantenimiento C. Inerte' },
        { field: 'anchura_libre_proyeccion_2', header: 'Anchura Libre Proyección I.' },
    ];

    public colsAva: ColInterface[] = [
        { field: 'finca', header: this.fincaName },
        { field: 'sector', header: this.sectorName },
        { field: 'parcelas', header: this.parcelaName },
        { field: 'tipo_ecorregimen', header: 'Tipo Ecorrégimen' },
        { field: 'fecha_seca_inicio_intermitente', header: 'Fecha Inicio Seca' },
        { field: 'fecha_seca_fin_intermitente', header: 'Fecha Fin Seca' },
    ];

    constructor(
        dashboard: DashboardService,
        private readonly router: Router,
        public tareasApi: TareasApiService,
    ) {
        super(
            dashboard,
            tareasApi.agroambientales.GET,
            tareasApi.agroambientales.DELETE,
        );

        this.getRequest = tareasApi.agroambientales.GET;
        this.deleteRequest = tareasApi.agroambientales.DELETE;
    }

    public add(): void {
        this.router.navigate(['dashboard', this.pathToForm]);
    }

    public edit(data: AgroambientalModel): void {
        this.router.navigate(['dashboard', this.pathToEdit, data.id]);
    }

    public duplicate(data: AgroambientalModel): void {
        this.router.navigate(['dashboard', this.pathToDuplicate, data.id]);
    }

    public override show(modal: string, data: AgroambientalModel): void {
        super.show(modal, data);

        const formType = modal === 'delete' ? +FormType.DELETION : +FormType.INFORMATIVE;

        this.form = new AgroambientalesFormDefinition(formType, data);
    }

    public override hide(modal: string): void {
        super.hide(modal);
        this.form = null;
    }

    public globalConditionalStyle(_value: string, _col: ColInterface): ColStylesInterface {
        return { textAlign: 'center' };
    }

    public downloadCsv(): void {
        this.form = new AgroambientalesFormDefinition(+FormType.EXPORT, {});

        const csvContent = createCsv(
            this.form.fieldNames,
            this.form.fieldLabels,
            this.filtering.value as AgroambientalModel[],
        );
        const csvLink = document.getElementById('csvLink') as HTMLAnchorElement | null;

        if (!csvLink) {
            return;
        }

        csvLink.download = `${this.form.formFields.name}.csv`;
        csvLink.href = encodeURI(csvContent);
        csvLink.click();
    }

    public globalVisualTransform(value: string, col: ColInterface, row: TreeNode<AgroambientalModel>): string {
        const agroambiental = row.data as AgroambientalModel;

        if (Utils.isNumber(value) && col.field !== 'finca') {
            return Utils.decimalFormat(value, 2, '.', ',', 3);
        }

        if (col.field === 'parcelas') {
            const textValue = Utils.replaceAll(
                Utils.replaceAll((agroambiental[col.field]?.toString() || ''), '- ', ''),
                '\n',
                '\n, ',
            );
            const normalizedValue = textValue.substring(0, textValue.length - 2);

            return normalizedValue.length > 50 ? normalizedValue.slice(0, 49) + '…' : normalizedValue;
        }

        if (col.field === 'fecha_seca_inicio_intermitente' || col.field === 'fecha_seca_fin_intermitente') {
            if (!value) {
                return '';
            }

            const invalidFormats = [
                /^\d{4}-\d{2}-\d{2}$/,
                /NaN/i,
            ];

            if (invalidFormats.some((pattern) => pattern.test(value))) {
                return '';
            }

            if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
                return '';
            }
        }

        return value;
    }
}
